import {
  parseAirshowStepProof,
  type AirshowStepProof,
} from "./airshowStepProofContract";

export interface AirshowSolidPreviewRequest {
  schema: "loom.airshow-solid-preview-request/v1";
  entry_id: string;
  archive_member_path: string;
  source_sha256: string;
  review_bundle_key: string;
  review_resolution: 12;
}

export interface AirshowSolidComponent {
  source_index: number;
  source_component_id: string;
  name: string;
  type: string;
}

export interface AirshowSolidOmittedComponent extends AirshowSolidComponent {
  code: string;
  message: string;
}

export interface AirshowSolidQc {
  vertices: number;
  triangles: number;
  boundary_edges: 0;
  nonmanifold_edges: 0;
  winding_consistent: true;
  self_intersections: 0;
  connected_components: number;
  positive_volume: true;
  signed_volume: number;
  canonical_qc_passed: true;
}

interface AirshowSolidPreviewBase {
  schema: "loom.airshow-solid-preview/v1";
  status: "available" | "blocked" | "unavailable";
  entry_id: string;
  archive_member_path: string;
  source_sha256: string;
  review_bundle_key: string;
  review_resolution: 12;
  solid_bundle_key: string | null;
  component_scope_complete: boolean;
  source_component_count: number;
  represented_components: readonly AirshowSolidComponent[];
  omitted_components: readonly AirshowSolidOmittedComponent[];
  warnings: readonly string[];
}

export interface AirshowSolidPreviewAvailable extends AirshowSolidPreviewBase {
  status: "available";
  solid_bundle_key: string;
  component_scope_complete: true;
  representation: "smooth-trimmed-step-assembly";
  fidelity: "exact-source-surfaces-tolerance-attested-fitted-trims";
  exact_solid: false;
  accepted_geometry: false;
  exportable: true;
  model_url: string;
  stl_url: string;
  step_url: string;
  receipt_url: string;
  model_sha256: string;
  stl_sha256: string;
  step_sha256: string;
  receipt_sha256: string;
  worker_fingerprint_sha256: string;
  qc: AirshowSolidQc;
  step_proof: AirshowStepProof;
}

interface AirshowSolidPreviewFailureBase extends AirshowSolidPreviewBase {
  solid_bundle_key: null;
  representation: "unavailable";
  fidelity: "unavailable";
  exact_solid: false;
  accepted_geometry: false;
  exportable: false;
  code: string;
  message: string;
  retryable: boolean;
}

export interface AirshowSolidPreviewBlocked extends AirshowSolidPreviewFailureBase {
  status: "blocked";
  component_scope_complete: true;
  retryable: false;
}

export interface AirshowSolidPreviewUnavailable extends AirshowSolidPreviewFailureBase {
  status: "unavailable";
  component_scope_complete: false;
}

export type AirshowSolidPreviewFailure =
  | AirshowSolidPreviewBlocked
  | AirshowSolidPreviewUnavailable;

export type AirshowSolidPreviewResponse =
  | AirshowSolidPreviewAvailable
  | AirshowSolidPreviewFailure;

type UnknownRow = Record<string, unknown>;

const COMMON_KEYS = [
  "schema", "status", "entry_id", "archive_member_path", "source_sha256",
  "review_bundle_key", "review_resolution", "solid_bundle_key", "source_component_count",
  "component_scope_complete", "represented_components", "omitted_components", "warnings",
] as const;
const AVAILABLE_KEYS = [
  ...COMMON_KEYS, "representation", "fidelity", "exact_solid", "accepted_geometry",
  "exportable", "model_url", "stl_url", "step_url", "receipt_url", "model_sha256",
  "stl_sha256", "step_sha256", "receipt_sha256", "worker_fingerprint_sha256", "qc",
  "step_proof",
] as const;
const FAILURE_KEYS = [
  ...COMMON_KEYS, "representation", "fidelity", "exact_solid", "accepted_geometry",
  "exportable", "code", "message", "retryable",
] as const;

function fail(message: string): never {
  throw new Error(`Airshow solid preview ${message}`);
}

function row(value: unknown, path: string): UnknownRow {
  if (!value || typeof value !== "object" || Array.isArray(value)) fail(`${path} must be an object`);
  return value as UnknownRow;
}

function exactKeys(value: UnknownRow, keys: readonly string[], path: string): void {
  const actual = Object.keys(value);
  if (actual.length !== keys.length || actual.some((key) => !keys.includes(key))) {
    fail(`${path} has an unsupported field set`);
  }
}

function text(value: unknown, path: string, maximum = 512): string {
  if (typeof value !== "string" || !value.trim() || value.length > maximum) {
    fail(`${path} must be bounded non-empty text`);
  }
  return value;
}

function digest(value: unknown, path: string): string {
  const result = text(value, path, 80);
  if (!/^[a-f0-9]{64}$/.test(result)) fail(`${path} must be a bare lowercase SHA-256 digest`);
  return result;
}

function integer(value: unknown, path: string, minimum: number, maximum: number): number {
  if (!Number.isInteger(value) || (value as number) < minimum || (value as number) > maximum) {
    fail(`${path} must be an integer from ${minimum} through ${maximum}`);
  }
  return value as number;
}

function code(value: unknown, path: string): string {
  const result = text(value, path, 128);
  if (!/^[a-z0-9][a-z0-9-]{0,127}$/.test(result)) fail(`${path} must be a canonical code`);
  return result;
}

function warnings(value: unknown): readonly string[] {
  if (!Array.isArray(value) || value.length > 64) fail("response.warnings must contain at most 64 items");
  return value.map((item, index) => text(item, `response.warnings[${index}]`, 1_024));
}

function component(value: unknown, path: string, omitted: false): AirshowSolidComponent;
function component(value: unknown, path: string, omitted: true): AirshowSolidOmittedComponent;
function component(
  value: unknown,
  path: string,
  omitted: boolean,
): AirshowSolidComponent | AirshowSolidOmittedComponent {
  const source = row(value, path);
  exactKeys(source, omitted
    ? ["source_index", "source_component_id", "name", "type", "code", "message"]
    : ["source_index", "source_component_id", "name", "type"], path);
  const base: AirshowSolidComponent = {
    source_index: integer(source.source_index, `${path}.source_index`, 0, 100_000),
    source_component_id: text(source.source_component_id, `${path}.source_component_id`, 256),
    name: text(source.name, `${path}.name`, 256),
    type: text(source.type, `${path}.type`, 128),
  };
  return omitted ? {
    ...base,
    code: code(source.code, `${path}.code`),
    message: text(source.message, `${path}.message`, 1_024),
  } : base;
}

function components(
  value: unknown,
  path: string,
  omitted: false,
): readonly AirshowSolidComponent[];
function components(
  value: unknown,
  path: string,
  omitted: true,
): readonly AirshowSolidOmittedComponent[];
function components(
  value: unknown,
  path: string,
  omitted: boolean,
): readonly (AirshowSolidComponent | AirshowSolidOmittedComponent)[] {
  if (!Array.isArray(value) || value.length > 100_000) fail(`${path} must be a bounded array`);
  return value.map((item, index) => omitted
    ? component(item, `${path}[${index}]`, true)
    : component(item, `${path}[${index}]`, false));
}

function validateFullScope(
  sourceCount: number,
  represented: readonly AirshowSolidComponent[],
  omitted: readonly AirshowSolidOmittedComponent[],
): void {
  if (represented.length + omitted.length !== sourceCount) {
    fail("response component scope does not match source_component_count");
  }
  const indices = [...represented, ...omitted].map(({ source_index: index }) => index).sort((a, b) => a - b);
  if (indices.some((index, position) => index !== position)) {
    fail("response component scope must cover every source index exactly once");
  }
}

function artifactUrl(
  value: unknown,
  projectId: string,
  bundleKey: string,
  filename: "model.gltf" | "solid.stl" | "assembly.step" | "receipt.json",
): string {
  const result = text(value, `response.${filename}`, 1_024);
  const expected = `/api/thread/v1/projects/${encodeURIComponent(projectId)}`
    + `/aero/vsp/airshow/solid-previews/${bundleKey}/${filename}`;
  if (result !== expected) fail(`response ${filename} URL is not bound to its project and solid bundle`);
  return result;
}

function qc(value: unknown): AirshowSolidQc {
  const source = row(value, "response.qc");
  exactKeys(source, [
    "vertices", "triangles", "boundary_edges", "nonmanifold_edges", "winding_consistent",
    "self_intersections", "connected_components", "positive_volume", "signed_volume",
    "canonical_qc_passed",
  ], "response.qc");
  if (source.boundary_edges !== 0 || source.nonmanifold_edges !== 0
      || source.winding_consistent !== true || source.self_intersections !== 0
      || !Number.isInteger(source.connected_components)
      || (source.connected_components as number) < 1
      || (source.connected_components as number) > 256 || source.positive_volume !== true
      || source.canonical_qc_passed !== true) {
    fail("response.qc does not prove a closed, consistently wound solid assembly");
  }
  if (typeof source.signed_volume !== "number" || !Number.isFinite(source.signed_volume)
      || source.signed_volume <= 0) {
    fail("response.qc.signed_volume must be finite and positive");
  }
  return {
    vertices: integer(source.vertices, "response.qc.vertices", 4, 10_000_000),
    triangles: integer(source.triangles, "response.qc.triangles", 4, 20_000_000),
    boundary_edges: 0,
    nonmanifold_edges: 0,
    winding_consistent: true,
    self_intersections: 0,
    connected_components: source.connected_components as number,
    positive_volume: true,
    signed_volume: source.signed_volume,
    canonical_qc_passed: true,
  };
}

export function createAirshowSolidPreviewRequest(input: {
  entryId: string;
  archiveMemberPath: string;
  sourceSha256: string;
  reviewBundleKey: string;
}): AirshowSolidPreviewRequest {
  return {
    schema: "loom.airshow-solid-preview-request/v1",
    entry_id: text(input.entryId, "request.entry_id", 512),
    archive_member_path: text(input.archiveMemberPath, "request.archive_member_path", 1_024),
    source_sha256: digest(input.sourceSha256, "request.source_sha256"),
    review_bundle_key: digest(input.reviewBundleKey, "request.review_bundle_key"),
    review_resolution: 12,
  };
}

export function parseAirshowSolidPreviewResponse(value: unknown, expected: {
  projectId: string;
  request: AirshowSolidPreviewRequest;
}): AirshowSolidPreviewResponse {
  const source = row(value, "response");
  if (source.schema !== "loom.airshow-solid-preview/v1") fail("response schema is unsupported");
  if (source.status !== "available" && source.status !== "blocked" && source.status !== "unavailable") {
    fail("response status is unsupported");
  }
  exactKeys(source, source.status === "available" ? AVAILABLE_KEYS : FAILURE_KEYS, "response");
  const request = expected.request;
  if (source.entry_id !== request.entry_id || source.archive_member_path !== request.archive_member_path
      || source.source_sha256 !== request.source_sha256
      || source.review_bundle_key !== request.review_bundle_key
      || source.review_resolution !== request.review_resolution) {
    fail("response identity does not match the request");
  }
  const sourceCount = integer(source.source_component_count, "response.source_component_count", 0, 100_000);
  const represented = components(source.represented_components, "response.represented_components", false);
  const omitted = components(source.omitted_components, "response.omitted_components", true);
  if (typeof source.component_scope_complete !== "boolean") {
    fail("response.component_scope_complete must be boolean");
  }
  if (source.status === "unavailable") {
    if (source.component_scope_complete !== false || sourceCount !== 0
        || represented.length !== 0 || omitted.length !== 0) {
      fail("unavailable response must not claim a derived component scope");
    }
  } else {
    if (source.component_scope_complete !== true) {
      fail("available or blocked response requires a complete component scope");
    }
    validateFullScope(sourceCount, represented, omitted);
    if (source.status === "available" && represented.length < 1) {
      fail("available response must represent at least one source component");
    }
  }
  const common = {
    schema: "loom.airshow-solid-preview/v1" as const,
    entry_id: request.entry_id,
    archive_member_path: request.archive_member_path,
    source_sha256: request.source_sha256,
    review_bundle_key: request.review_bundle_key,
    review_resolution: 12 as const,
    component_scope_complete: source.component_scope_complete,
    source_component_count: sourceCount,
    represented_components: represented,
    omitted_components: omitted,
    warnings: warnings(source.warnings),
  };
  if (source.status === "available") {
    if (source.representation !== "smooth-trimmed-step-assembly"
        || source.fidelity !== "exact-source-surfaces-tolerance-attested-fitted-trims"
        || source.exact_solid !== false
        || source.accepted_geometry !== false || source.exportable !== true) {
      fail("available response makes an unsupported geometry claim");
    }
    const bundleKey = digest(source.solid_bundle_key, "response.solid_bundle_key");
    const modelSha256 = digest(source.model_sha256, "response.model_sha256");
    const stlSha256 = digest(source.stl_sha256, "response.stl_sha256");
    const stepSha256 = digest(source.step_sha256, "response.step_sha256");
    const workerFingerprintSha256 = digest(
      source.worker_fingerprint_sha256,
      "response.worker_fingerprint_sha256",
    );
    const solidQc = qc(source.qc);
    let stepProof: AirshowStepProof;
    try {
      stepProof = parseAirshowStepProof(source.step_proof, {
        sourceSha256: request.source_sha256,
        workerFingerprintSha256,
        stepSha256,
        representedComponentIds: represented.map(({ source_component_id: id }) => id),
        connectedComponents: solidQc.connected_components,
      });
    } catch (error) {
      fail(`response.step_proof is invalid: ${
        error instanceof Error ? error.message : "unknown proof error"
      }`);
    }
    return {
      ...common,
      status: "available",
      solid_bundle_key: bundleKey,
      component_scope_complete: true,
      representation: "smooth-trimmed-step-assembly",
      fidelity: "exact-source-surfaces-tolerance-attested-fitted-trims",
      exact_solid: false,
      accepted_geometry: false,
      exportable: true,
      model_url: artifactUrl(source.model_url, expected.projectId, bundleKey, "model.gltf"),
      stl_url: artifactUrl(source.stl_url, expected.projectId, bundleKey, "solid.stl"),
      step_url: artifactUrl(source.step_url, expected.projectId, bundleKey, "assembly.step"),
      receipt_url: artifactUrl(source.receipt_url, expected.projectId, bundleKey, "receipt.json"),
      model_sha256: modelSha256,
      stl_sha256: stlSha256,
      step_sha256: stepSha256,
      receipt_sha256: digest(source.receipt_sha256, "response.receipt_sha256"),
      worker_fingerprint_sha256: workerFingerprintSha256,
      qc: solidQc,
      step_proof: stepProof,
    };
  }
  if (source.solid_bundle_key !== null || source.representation !== "unavailable"
      || source.fidelity !== "unavailable" || source.exact_solid !== false
      || source.accepted_geometry !== false || source.exportable !== false
      || typeof source.retryable !== "boolean") {
    fail("failed response exposes artifacts or unsupported geometry claims");
  }
  if (source.status === "blocked" && source.retryable !== false) {
    fail("blocked response must not claim it is retryable");
  }
  const failure = {
    ...common,
    solid_bundle_key: null,
    representation: "unavailable" as const,
    fidelity: "unavailable" as const,
    exact_solid: false as const,
    accepted_geometry: false as const,
    exportable: false as const,
    code: code(source.code, "response.code"),
    message: text(source.message, "response.message", 2_048),
  };
  return source.status === "blocked" ? {
    ...failure,
    status: "blocked",
    component_scope_complete: true,
    retryable: false,
  } : {
    ...failure,
    status: "unavailable",
    component_scope_complete: false,
    retryable: source.retryable,
  };
}
